// Kapitelübergreifendes Profil (Taler, Ausbau, Erfolge, Statistik, Logbuch) – lokal gespeichert, Store für die UI.
import { writable, get } from 'svelte/store';
import { newProfile, recordAnswer, checkAchievements, buy, collect as collectMeta, bump, greeting, noteVisit, noteTalk, noteGame, type Profile, type LogEntry, type Greeting } from '../../shared/meta.ts';
import type { Question } from '../../shared/types.ts';
import { solutionText } from '../../shared/quiz.ts';
import { feature } from '../../shared/features.ts';
import { t } from '../i18n.ts';
import { audio } from '../audio/audio.ts';
import { markLocalChange } from './cloud.ts';

const KEY = 'dlr.profile';

function load(): Profile {
  try {
    const s = localStorage.getItem(KEY);
    if (s) return { ...newProfile(), ...JSON.parse(s) };
  } catch {
    /* neu */
  }
  return newProfile();
}

export const profile = writable<Profile>(load());
export const toasts = writable<{ id: number; text: string; kind: 'achievement' | 'coins' | 'collect' }[]>([]);
let tid = 0;

export function toast(text: string, kind: 'achievement' | 'coins' | 'collect' = 'achievement') {
  const id = ++tid;
  toasts.update((x) => [...x.slice(-3), { id, text, kind }]);
  setTimeout(() => toasts.update((x) => x.filter((y) => y.id !== id)), 4200);
}

/** nach jeder Änderung: speichern, Erfolge prüfen, melden */
export function mutate(fn: (p: Profile) => void) {
  if (!feature.meta) return;
  const p = get(profile);
  fn(p);
  for (const a of checkAchievements(p)) {
    toast(`🏆 ${t('ach.unlocked')}: ${t('ach.' + a + '.name')}`);
    audio.sfx('right');
  }
  try {
    localStorage.setItem(KEY, JSON.stringify(p));
  } catch {
    /* privat */
  }
  profile.set({ ...p });
  markLocalChange();
}

export function logAnswer(q: Question, right: boolean, mode: LogEntry['mode']) {
  mutate((p) =>
    recordAnswer(p, {
      qid: q.id,
      prompt: q.prompt,
      category: q.category.startsWith('Themenrunde') ? 'Themenrunden' : q.category,
      answer: solutionText(q),
      right,
      explanation: q.explanation,
      source: { title: q.source.title, url: q.source.url },
      mode,
    }),
  );
}

export function buyUpgrade(id: string): boolean {
  let ok = false;
  mutate((p) => (ok = buy(p, id)));
  if (ok) audio.sfx('combine');
  return ok;
}

export function collect(id: string): boolean {
  let ok = false;
  mutate((p) => (ok = collectMeta(p, id)));
  if (ok) {
    toast(`🍺 ${t('meta.collected')} (+15 ${t('meta.coins')})`, 'collect');
    audio.sfx('pickup');
  }
  return ok;
}

/** Trinkgeld/Belohnung in Talern */
export function reward(n: number) {
  mutate((p) => {
    p.coins += n;
    p.earned += n;
  });
  if (feature.meta) toast(`🪙 +${n} ${t('meta.coins')}`, 'coins');
}

export function count(key: string) {
  mutate((p) => bump(p, key));
}

// ---------------- Kneipenleben (S9) ----------------

/** Stammgast begrüßt Mara aus dem Gedächtnis; danach ist die Begegnung gemerkt */
export function meetGuest(npc: string): Greeting {
  const g = greeting(get(profile), npc);
  mutate((p) => noteVisit(p, npc));
  return g;
}

export function talked(npc: string, said: string) {
  mutate((p) => noteTalk(p, npc, said));
}

/** Story-Weg öffnen/abschließen; Belohnung in Talern */
export function unlockStory(id: string, coins = 0): boolean {
  let fresh = false;
  mutate((p) => {
    p.story ??= {};
    if (p.story[id]) return;
    p.story[id] = Date.now();
    fresh = true;
    p.coins += coins;
    p.earned += coins;
  });
  if (fresh && feature.meta) {
    toast(`🧭 ${t('story.unlocked')}: ${t('story.' + id)}${coins ? ` (+${coins} ${t('meta.coins')})` : ''}`, 'collect');
    audio.sfx('pickup');
  }
  return fresh;
}

/** Kneipenspiel gegen einen Stammgast beendet */
export function gameResult(npc: string, game: string, won: boolean) {
  mutate((p) => {
    noteGame(p, npc, game, won);
    if (won) {
      p.coins += 20;
      p.earned += 20;
    }
  });
  if (won && feature.meta) toast(`🎲 ${t('games.wonToast', { name: t('npcName.' + npc) })} (+20 ${t('meta.coins')})`, 'coins');
  // Brakenhoff schlägt man nicht folgenlos: Er rückt die Pokal-Replik heraus
  if (won && npc === 'brakenhoff' && game === 'Schocken') unlockStory('brakenhoff');
}

/** für Tests: Profil zurücksetzen */
export function resetProfile() {
  profile.set(newProfile());
  try {
    localStorage.removeItem(KEY);
  } catch {
    /* egal */
  }
}
